import { createSlice } from '@reduxjs/toolkit';
import { SEVERITY } from '../constants';
import { localStorage } from '../env/browser';
import {
  initNotificationCounts,
  newNotificationCounts,
  initNotificationClientMessages,
  notificationsClientMessageCloseClick,
  notificationsAppointmentClick,
  notificationsMessageClick,
  notificationsAppointmentItemClick,
  notificationsAppointmentCloseClick,
  notificationsAppointmentFetchSuccess,
  notificationsAppointmentFetchError,
  documentClick,
  notificationSMSDisplaySeverity,
} from '../actions';
import type { AppointmentNotification } from '../actions';

interface ClientMessage {
  id: string;
  closed: boolean;
}

interface NotificationsUiState {
  open: boolean;
  error: boolean;
  loaded: boolean;
}

export interface NotificationsState {
  appointmentCount: number;
  messageCount: number;
  appointments: AppointmentNotification[];
  clientMessages: ClientMessage[];
  ui: NotificationsUiState;
  severity: string;
}

const removeBy = <T>(predicate: (item: T) => boolean, array: T[]): T[] => {
  const index = array.findIndex(predicate);
  return index !== -1
    ? [...array.slice(0, index), ...array.slice(index + 1)]
    : array;
};

const initialState: NotificationsState = {
  appointmentCount: 0,
  messageCount: 0,
  appointments: [],
  clientMessages: [],
  ui: { open: false, error: false, loaded: false },
  severity: localStorage.getItem(SEVERITY.LABEL) || SEVERITY.NONE,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initNotificationCounts, (state, action) => {
        state.appointmentCount = action.payload.appointments;
        state.messageCount = action.payload.messages;
      })
      .addCase(newNotificationCounts, (state, action) => {
        state.appointmentCount = action.payload.appointments;
        state.messageCount = action.payload.messages;
      })
      .addCase(initNotificationClientMessages, (state, action) => {
        state.clientMessages = action.payload.map((id) => ({
          id,
          closed: false,
        }));
      })
      .addCase(notificationsClientMessageCloseClick, (state, action) => {
        state.clientMessages = state.clientMessages.map((m) => ({
          ...m,
          closed: m.id === action.payload ? true : m.closed,
        }));
      })
      .addCase(notificationsAppointmentClick, (state) => {
        state.ui.open = !state.ui.open;
      })
      .addCase(notificationsMessageClick, (state) => {
        state.messageCount = 0;
      })
      .addCase(notificationsAppointmentItemClick, (state, action) => {
        state.appointmentCount = Math.max(state.appointmentCount - 1, 0);
        state.appointments = removeBy(
          (item) => item.id === action.payload.id,
          state.appointments
        );
        state.ui.open = false;
      })
      .addCase(notificationsAppointmentCloseClick, (state, action) => {
        const count = Math.max(state.appointmentCount - 1, 0);
        state.appointmentCount = count;
        state.appointments = removeBy(
          (item) => item.id === action.payload,
          state.appointments
        );
        state.ui.open = count > 0 ? state.ui.open : false;
      })
      .addCase(notificationsAppointmentFetchSuccess, (state, action) => {
        state.appointmentCount =
          action.payload.length === 0 ? 0 : state.appointmentCount;
        state.appointments = action.payload;
        state.ui.error = false;
        state.ui.loaded = true;
      })
      .addCase(notificationsAppointmentFetchError, (state) => {
        state.ui.error = true;
        state.ui.loaded = true;
      })
      .addCase(documentClick, (state) => {
        if (state.ui.open) {
          state.ui.open = false;
        }
      })
      .addCase(notificationSMSDisplaySeverity, (state, action) => {
        state.severity = action.payload;
      });
  },
});

export const notificationsReducer = notificationsSlice.reducer;
